// Lib
import { CSSProperties, RefObject, useMemo } from 'react';

// Utils
import { getCroppedImageHeight, getCroppedImageWidth } from '../../imageModalUtils';
import { useMeasuredElementRect } from '../../../../../utils/dom/useMeasuredElementRect';

// Types
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';
import { RectSize } from '../../../../../../common/maths/geometry/rect/rectTypes';

const MIN_PRIMARY_TOOLBAR_HEIGHT = 48;
const TOOLBAR_BOTTOM_GAP = 20;
const IMAGE_AREA_HORIZONTAL_PADDING = 60;
const IMAGE_AREA_VERTICAL_PADDING = 40;

type ImageModalV2LayoutProps = {
    element: ImMNElement;
    attachment?: Record<string, unknown>;
    gridSize: number;
    modalRef: RefObject<HTMLElement>;
    primaryToolbarRef: RefObject<HTMLElement>;
    captionRef: RefObject<HTMLElement>;
};

type ImageModalV2Layout = {
    imageAreaStyle: CSSProperties;
    contentStyle: CSSProperties;
    imageWrapperStyle: CSSProperties;
    toolbarStyle: CSSProperties;
};

type ImageModalV2LayoutCalculation = {
    modalSize: RectSize;
    imageSize: RectSize;
    primaryToolbarSize: RectSize;
    captionSize: RectSize;
};

const fitImageWithinBounds = ({ imageSize, bounds }: { imageSize: RectSize; bounds: RectSize }): RectSize => {
    if (!imageSize.width || !imageSize.height || !bounds.width || !bounds.height) return { width: 0, height: 0 };

    const scale = Math.min(1, bounds.width / imageSize.width, bounds.height / imageSize.height);

    return {
        width: Math.round(imageSize.width * scale),
        height: Math.round(imageSize.height * scale),
    };
};

/**
 * Calculates V2 image modal geometry from measured DOM sizes.
 *
 * This is the only place that decides how much room the image gets. CSS provides the shell,
 * while these measured values reserve stable space for the primary toolbar and caption before
 * fitting the image into the remaining viewport area.
 */
export const calculateImageModalV2Layout = ({
    modalSize,
    imageSize,
    primaryToolbarSize,
    captionSize,
}: ImageModalV2LayoutCalculation) => {
    const primaryToolbarHeight = primaryToolbarSize.height || MIN_PRIMARY_TOOLBAR_HEIGHT;
    const captionHeight = captionSize.height || 0;
    const imageAreaHeight = Math.max(modalSize.height - primaryToolbarHeight, 0);
    const imageBounds = {
        width: Math.max(modalSize.width - IMAGE_AREA_HORIZONTAL_PADDING * 2, 0),
        height: Math.max(imageAreaHeight - IMAGE_AREA_VERTICAL_PADDING * 2 - captionHeight, 0),
    };
    const fittedImageSize = fitImageWithinBounds({ imageSize, bounds: imageBounds });

    return {
        imageAreaStyle: {
            flexBasis: imageAreaHeight,
            height: imageAreaHeight,
            paddingTop: IMAGE_AREA_VERTICAL_PADDING,
            paddingRight: IMAGE_AREA_HORIZONTAL_PADDING,
            paddingBottom: IMAGE_AREA_VERTICAL_PADDING,
            paddingLeft: IMAGE_AREA_HORIZONTAL_PADDING,
        },
        contentStyle: {
            width: fittedImageSize.width,
            height: fittedImageSize.height + captionHeight,
        },
        imageWrapperStyle: fittedImageSize,
        toolbarStyle: {
            bottom: TOOLBAR_BOTTOM_GAP,
        },
    };
};

/**
 * Builds the measured layout used by the V2 image modal shell.
 */
export const useImageModalV2Layout = ({
    element,
    attachment,
    gridSize,
    modalRef,
    primaryToolbarRef,
    captionRef,
}: ImageModalV2LayoutProps): ImageModalV2Layout => {
    // These are the layout inputs that can change after first render:
    // viewport/modal size, primary toolbar height and TipTap caption height.
    const modalSize = useMeasuredElementRect(modalRef);
    const primaryToolbarSize = useMeasuredElementRect(primaryToolbarRef);
    const captionSize = useMeasuredElementRect(captionRef);

    // The source image dimensions come from image metadata and crop state, not from the rendered DOM.
    const imageSize = useMemo(
        () => ({
            width: getCroppedImageWidth({ element, gridSize, attachment }),
            height: getCroppedImageHeight({ element, gridSize, attachment }),
        }),
        [attachment, element, gridSize],
    );

    // Once the measurements are known, layout becomes a pure calculation that returns inline styles
    // for the image area, content wrapper, image itself and toolbar position.
    return useMemo(
        () => calculateImageModalV2Layout({ modalSize, imageSize, primaryToolbarSize, captionSize }),
        [captionSize, imageSize, modalSize, primaryToolbarSize],
    );
};
